// src/modules/app-views/_apps/appmulti/leads/pages/Document/tabs/QuotationsTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Button, Table, Tooltip } from 'antd';
import { TagsOutlined } from '@ant-design/icons';
import { FaPlaneArrival, FaPlaneDeparture } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { encodeQuery } from 'utils/urlUtils';
import translateX from 'utils/translateX';
import { useDocumentContext } from 'components/core/DocumentProvider';
import leadsService from '../../../../service';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const QUOTATIONS_ROUTE = '/app/commercial/travel-insurance-quotations';

// *******************************************************************************
// HELPERS
// *******************************************************************************
const PassengerCell = ({ passengers }) => (
  <span className="d-inline-flex gap-1">
    {(passengers || []).map((age, i) => {
      const AgeIcon = leadsService.getAgeIcon(age);
      return (
        <Tooltip key={i} title={translateX('age_year_old', { AGE: age })}>
          <span><AgeIcon /></span>
        </Tooltip>
      );
    })}
  </span>
);

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const QuotationsTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data } = useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const navigate = useNavigate();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const dataSource = [...(data?.customData?.quotations || [])].sort(
    (a, b) => dayjs(b?.createdAt).valueOf() - dayjs(a?.createdAt).valueOf(),
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const onClickQuotePrice = record => {
    const qs = encodeQuery({
      destiny: record.destiny,
      departure: record.departure ? dayjs(record.departure).format('YYYY-MM-DD') : undefined,
      arrival: record.arrival ? dayjs(record.arrival).format('YYYY-MM-DD') : undefined,
      passengers: record.passengers,
    });
    navigate(`${QUOTATIONS_ROUTE}?${qs}`);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = [
    {
      key: 'quote_in',
      title: translateX('quote_in'),
      width: 160,
      render: (_, r) => (r?.createdAt ? dayjs(r.createdAt).format('DD/MM/YYYY HH:mm') : '—'),
    },
    {
      key: 'destiny',
      title: translateX('destiny'),
      render: (_, r) => (r?.destiny ? translateX(r.destiny) : '—'),
    },
    {
      key: 'dates',
      title: translateX('travel_period'),
      render: (_, r) => (
        <span>
          <FaPlaneDeparture className="mr-1" />
          {r?.departure ? dayjs(r.departure).format('DD/MM/YYYY') : '—'}
          <FaPlaneArrival className="ml-2 mr-1" />
          {r?.arrival ? dayjs(r.arrival).format('DD/MM/YYYY') : '—'}
        </span>
      ),
    },
    {
      key: 'passengers',
      title: translateX('passengers'),
      render: (_, r) => <PassengerCell passengers={r?.passengers} />,
    },
    {
      key: 'actions',
      title: '',
      width: 60,
      align: 'center',
      render: (_, r) => (
        <Tooltip title={translateX('quote_price')}>
          <Button type="text" icon={<TagsOutlined />} onClick={() => onClickQuotePrice(r)} />
        </Tooltip>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Table
      rowKey="_id"
      dataSource={dataSource}
      columns={columns}
      pagination={false}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default QuotationsTab;
